import { useStories } from '@/store/modules/App/stories'
import { isBlockContent } from '@/types/content'
import type { BlockContent } from '@/types/content'
import { getBlockTemplate, getBlocksInStory } from '@/utils/getters'
import { numberFromString } from '@/utils/parsers'
import { getSanitizedComponentName } from '@/utils/specifics'
import type {
  BlockContentWithParent,
  RecordStrategy,
  RecordStrategyArgs,
  SearchableData,
} from '../types'

export class BlocksRecord implements RecordStrategy {
  async get({ spaceId, storyId }: RecordStrategyArgs): Promise<BlockContent[]> {
    if (!spaceId) {
      return []
    }

    try {
      const storyIdParam = storyId?.toString()
      if (storyIdParam === undefined) {
        throw new Error('Story ID is required')
      }
      const storyIdNumber = numberFromString(storyIdParam)
      if (storyIdNumber === undefined) {
        throw new Error('Story ID must be a valid number')
      }

      const story = await useStories().loadStorySimple({
        spaceId,
        storyId: storyIdNumber,
      })

      if (isBlockContent(story.content)) {
        return getBlocksInStory(story.content)
      }
      return []
    } catch (error) {
      console.error('Error fetching blocks', error)
      return []
    }
  }

  async set(args: RecordStrategyArgs = {}): Promise<void> {
    const { spaceId, storyId } = args
    if (!spaceId || !storyId) {
      return
    }
    const blocksData = await this.get(args)
    window?.CommandBar?.addRecords(
      'blocks',
      this.createSearchableRecord(blocksData, spaceId, storyId),
      {
        detail: { type: 'component', value: 'record-preview-with-image' },
        searchableFields: ['label', 'technical_name'],
      },
    )
  }

  remove(): void {
    window?.CommandBar?.addRecords('blocks', [])
  }

  getBlockLabel(block: BlockContentWithParent): string {
    if (!block.parent_name) {
      return getSanitizedComponentName(block.component)
    }
    return `${getSanitizedComponentName(block.parent_name)} · ${getSanitizedComponentName(block.component)}`
  }

  createSearchableRecord(
    record: BlockContentWithParent[],
    spaceId: string,
    storyId: string,
  ): SearchableData[] {
    return record.map((block) => ({
      label: this.getBlockLabel(block),
      title: block.component,
      technical_name: block.component,
      id: block._uid,
      preview: getBlockTemplate(block),
      url: `/#/me/spaces/${spaceId}/stories/0/0/${storyId}/blok/${block._uid}`,
    }))
  }
}
